import React from "react";
import { CONTACT } from "../data/cruiseData";

/**
 * SEOHead , single source of truth for per-page SEO tags.
 *
 * Injects into <head>:
 *  - <title>
 *  - <meta name="description">
 *  - <meta name="robots">
 *  - <meta name="keywords"> (optional)
 *  - Open Graph tags (og:title, og:description, og:image, og:type, og:url, og:site_name, og:locale)
 *  - Twitter Card tags (twitter:card, twitter:title, twitter:description, twitter:image)
 *  - <link rel="canonical">
 *  - JSON-LD: base WebPage + provider, optional FAQPage, optional LocalBusiness,
 *    optional BreadcrumbList, optional Event schema (for ticketed cruises).
 *
 * NOTE: The `magiccruises.com.au` apex is hard-coded for canonicals on prod ,
 * preview/staging URLs are auto-normalised to the production origin so Google
 * never indexes the preview environment.
 */
const PROD_ORIGIN = "https://magiccruises.com.au";

function normaliseCanonical(canonical) {
 try {
  const url = canonical ? new URL(canonical, window.location.href) : new URL(window.location.href);
  // Always serve canonicals from the production origin to consolidate ranking signals.
  return `${PROD_ORIGIN}${url.pathname}${url.search || ""}`;
 } catch {
  return canonical || `${PROD_ORIGIN}/`;
 }
}

function upsertMeta(attr, key, value) {
 if (!value) return;
 let el = document.querySelector(`meta[${attr}="${key}"]`);
 if (!el) {
  el = document.createElement("meta");
  el.setAttribute(attr, key);
  document.head.appendChild(el);
 }
 el.setAttribute("content", value);
}

function writeSchema(id, obj) {
 let s = document.getElementById(id);
 if (!s) {
  s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  document.head.appendChild(s);
 }
 s.textContent = JSON.stringify(obj);
}

function removeSchema(id) {
 const s = document.getElementById(id);
 if (s) s.remove();
}

export default function SEOHead({
 title,
 description,
 image,
 type = "WebPage",
 canonical,
 keywords,
 schemaExtra = {},
 faqItems = null,             // [{ q, a }] -> FAQPage schema
 includeLocalBusiness = false,
 breadcrumbs = null,          // [{ name, url }] -> BreadcrumbList schema
 eventSchema = null,          // single event object -> Event schema (for ticketed cruises)
 noindex = false,
}) {
 React.useEffect(() => {
  if (title) document.title = title;
  if (description) upsertMeta("name", "description", description);
  if (keywords) upsertMeta("name", "keywords", keywords);
  upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

  const canonHref = normaliseCanonical(canonical);
  let canon = document.querySelector('link[rel="canonical"]');
  if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
  canon.href = canonHref;

  // Open Graph
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:image", image);
  upsertMeta("property", "og:type", type === "Article" || type === "BlogPosting" ? "article" : "website");
  upsertMeta("property", "og:url", canonHref);
  upsertMeta("property", "og:site_name", "Magic Cruises Sydney Harbour");
  upsertMeta("property", "og:locale", "en_AU");

  // Twitter Card
  upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", image);

  // 1) Base WebPage schema
  const baseSchema = {
   "@context": "https://schema.org",
   "@type": type,
   name: title,
   description,
   image,
   url: canonHref,
   inLanguage: "en-AU",
   provider: {
    "@type": "TouristAttraction",
    name: "Magic Cruises Sydney Harbour",
    telephone: CONTACT.phone1,
    email: CONTACT.email,
    address: { "@type": "PostalAddress", streetAddress: "King St Wharf 3", addressLocality: "Sydney", addressRegion: "NSW", postalCode: "2000", addressCountry: "AU" },
    geo: { "@type": "GeoCoordinates", latitude: -33.8688, longitude: 151.2018 },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "500" },
   },
   ...schemaExtra,
  };
  writeSchema("ld-magic-page", baseSchema);

  // 2) FAQPage schema
  if (Array.isArray(faqItems) && faqItems.length) {
   const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(({ q, a }) => ({
     "@type": "Question",
     name: q,
     acceptedAnswer: { "@type": "Answer", text: a },
    })),
   };
   writeSchema("ld-magic-faq", faqSchema);
  } else {
   removeSchema("ld-magic-faq");
  }

  // 3) LocalBusiness schema for rich local results
  if (includeLocalBusiness) {
   const lb = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: "Magic Cruises Sydney Harbour",
    description: "Magic Cruises is Sydney's most loved private boat charter on Sydney Harbour. Sydney Harbour cruises for weddings, corporate events, birthdays, hens parties, engagements and milestone celebrations from King St Wharf 3 in Darling Harbour.",
    image,
    url: PROD_ORIGIN,
    telephone: CONTACT.phone1,
    email: CONTACT.email,
    priceRange: "$$$",
    address: {
     "@type": "PostalAddress",
     streetAddress: "King St Wharf 3, Lime St",
     addressLocality: "Sydney",
     addressRegion: "NSW",
     postalCode: "2000",
     addressCountry: "AU",
    },
    geo: { "@type": "GeoCoordinates", latitude: -33.8688, longitude: 151.2018 },
    openingHoursSpecification: [
     { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], opens: "09:00", closes: "23:00" },
    ],
    sameAs: [
     "https://www.instagram.com/magic_cruises_sydney/",
     "https://www.facebook.com/magiccruisessydney/",
    ],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "500" },
    areaServed: { "@type": "City", name: "Sydney" },
   };
   writeSchema("ld-magic-local", lb);
  } else {
   removeSchema("ld-magic-local");
  }

  // 4) BreadcrumbList schema
  if (Array.isArray(breadcrumbs) && breadcrumbs.length) {
   const bc = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
     "@type": "ListItem",
     position: i + 1,
     name: b.name,
     item: b.url?.startsWith("http") ? b.url : `${PROD_ORIGIN}${b.url || "/"}`,
    })),
   };
   writeSchema("ld-magic-breadcrumb", bc);
  } else {
   removeSchema("ld-magic-breadcrumb");
  }

  // 5) Event schema (ticketed cruises)
  if (eventSchema && eventSchema.name && eventSchema.startDate) {
   const ev = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: eventSchema.name,
    startDate: eventSchema.startDate,
    endDate: eventSchema.endDate || eventSchema.startDate,
    eventStatus: eventSchema.eventStatus || "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
     "@type": "Place",
     name: "King St Wharf 3, Darling Harbour",
     address: {
      "@type": "PostalAddress",
      streetAddress: "King St Wharf 3, Lime St",
      addressLocality: "Sydney",
      addressRegion: "NSW",
      postalCode: "2000",
      addressCountry: "AU",
     },
    },
    image,
    description: eventSchema.description || description,
    organizer: {
     "@type": "Organization",
     name: "Magic Cruises Sydney Harbour",
     url: PROD_ORIGIN,
    },
    ...(eventSchema.offers ? {
     offers: {
      "@type": "Offer",
      url: canonHref,
      priceCurrency: "AUD",
      price: String(eventSchema.offers.price),
      availability: eventSchema.offers.availability || "https://schema.org/InStock",
      validFrom: eventSchema.offers.validFrom || new Date().toISOString(),
     },
    } : {}),
   };
   writeSchema("ld-magic-event", ev);
  } else {
   removeSchema("ld-magic-event");
  }
 }, [title, description, image, type, canonical, keywords, schemaExtra, faqItems, includeLocalBusiness, breadcrumbs, eventSchema, noindex]);

 return null;
}
